import { Component } from '@theme/component';

const STORAGE_KEY = 'atc-breadcrumbs:last-page';
const ROOT_PATHS = new Set(['/', '']);
const HISTORY_SOURCE_TYPES = new Set(['page', 'collection']);
const HISTORY_TARGET_TYPES = new Set(['collection', 'product', 'page', 'metaobject']);
const REFERRER_TITLE_SELECTORS = [
  '[data-atc-breadcrumbs]',
  'main h1',
  'h1',
];

const getStoredPage = () => {
  try {
    const storedPage = sessionStorage.getItem(STORAGE_KEY);
    if (!storedPage) return null;
    return JSON.parse(storedPage);
  } catch {
    return null;
  }
};

const setStoredPage = (page) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(page));
  } catch {
    return;
  }
};

const getNormalizedUrl = (url) => {
  if (!url) return null;

  try {
    const parsedUrl = new URL(url, window.location.origin);
    if (parsedUrl.origin !== window.location.origin) return null;
    parsedUrl.hash = '';
    return parsedUrl;
  } catch {
    return null;
  }
};

const isUsefulHistoryPage = (previousPage, currentPage) => {
  if (!previousPage || !currentPage) return false;
  if (!HISTORY_SOURCE_TYPES.has(previousPage.pageType)) return false;
  if (!HISTORY_TARGET_TYPES.has(currentPage.pageType)) return false;

  const previousUrl = getNormalizedUrl(previousPage.url);
  const currentUrl = getNormalizedUrl(currentPage.url);
  if (!previousUrl || !currentUrl) return false;
  if (previousUrl.href === currentUrl.href) return false;
  if (ROOT_PATHS.has(previousUrl.pathname)) return false;

  return Boolean(previousPage.title);
};

const getPageTypeFromPathname = (pathname) => {
  if (pathname.startsWith('/collections/')) return 'collection';
  if (pathname.startsWith('/pages/')) return 'page';
  if (pathname.startsWith('/blogs/')) return 'page';
  return '';
};

const getTitleFromReferrerDocument = (doc) => {
  for (const selector of REFERRER_TITLE_SELECTORS) {
    const element = doc.querySelector(selector);
    if (!element) continue;

    if (element instanceof HTMLElement && element.dataset.currentTitle) {
      return element.dataset.currentTitle;
    }

    const text = element.textContent?.trim();
    if (text) return text;
  }

  return doc.title.replace(/\s+[|–-]\s+.*$/, '').trim();
};

const getReferrerPage = async () => {
  const referrerUrl = getNormalizedUrl(document.referrer);
  if (!referrerUrl) return null;
  if (ROOT_PATHS.has(referrerUrl.pathname)) return null;

  try {
    const response = await fetch(referrerUrl.href, { credentials: 'same-origin' });
    if (!response.ok) return null;

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const breadcrumbData = doc.querySelector('[data-atc-breadcrumbs]');
    const pageType =
      breadcrumbData instanceof HTMLElement && breadcrumbData.dataset.pageType
        ? breadcrumbData.dataset.pageType
        : getPageTypeFromPathname(referrerUrl.pathname);
    const title = getTitleFromReferrerDocument(doc);

    return {
      pageType,
      title,
      url: referrerUrl.href,
    };
  } catch {
    return null;
  }
};

const getCurrentPage = (breadcrumbs) => {
  const currentUrl = getNormalizedUrl(breadcrumbs.dataset.currentUrl);
  if (!currentUrl) return null;

  return {
    pageType: breadcrumbs.dataset.pageType || '',
    title: breadcrumbs.dataset.currentTitle || document.title,
    url: currentUrl.href,
  };
};

/**
 * Enhances server-rendered breadcrumbs with an optional previous-page crumb.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [liquidParentItem]
 * @property {HTMLElement} [historyItem]
 * @property {HTMLAnchorElement} [historyLink]
 *
 * @extends {Component<Refs>}
 */
class AtcBreadcrumbsComponent extends Component {
  #initialized = false;

  connectedCallback() {
    super.connectedCallback();

    if (this.#initialized) return;
    this.#initialized = true;

    this.#init();
  }

  async #init() {
    const currentPage = getCurrentPage(this);
    if (!currentPage) return;

    let previousPage = getStoredPage();
    if (!previousPage) {
      previousPage = await getReferrerPage();
    }

    if (!this.isConnected) return;

    if (this.dataset.historyEnabled === 'true' && isUsefulHistoryPage(previousPage, currentPage)) {
      this.#setHistoryCrumb(previousPage);
    }

    setStoredPage(currentPage);
  }

  #setHistoryCrumb(previousPage) {
    const historyItem = this.refs.historyItem;
    const historyLink = this.refs.historyLink;
    if (!historyItem || !(historyLink instanceof HTMLAnchorElement)) return;

    historyLink.href = previousPage.url;
    historyLink.textContent = previousPage.title;
    historyItem.hidden = false;

    if (this.refs.liquidParentItem) {
      this.refs.liquidParentItem.hidden = true;
    }
  }
}

if (!customElements.get('atc-breadcrumbs-component')) {
  customElements.define('atc-breadcrumbs-component', AtcBreadcrumbsComponent);
}
